import { useState } from 'react';
import { useDisclosure } from '@podiumhq/design-system';
import { useTracking } from '@podiumhq/tracking';

import {
  CANADIAN_COUNTRY_CODE,
  CANADIAN_PROVINCES,
  US_STATES,
} from 'components/BusinessDetails';
import { useCheckoutContext } from 'contexts/CheckoutContextProvider';
import {
  ADDRESS_FIELD,
  ADDRESS_OPTIONAL_FIELD,
  CITY_FIELD,
  COUNTRY_FIELD,
  POSTAL_CODE_FIELD,
  STATE_FIELD,
} from 'components/FormElements/constants';

import { BILLING_ADDRESS } from '../../constants';
import { useGetBillingAddress } from '../../hooks';
import { BillingAddressFormWrapper } from '../../styles';

import BillingDetails from '../BillingDetails';
import { AddressInput } from '../FormItems';

import { EditModalWrapper } from './modals';
import { isEqual } from 'lodash';

export const BillingDetailsSummary = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const addressDetails = useGetBillingAddress();
  const {
    setBillingAddress,
    setSelectedBillingAddress,
    validateBillingAddress,
  } = useCheckoutContext();
  const { trackEvent } = useTracking();

  const [addressToUpdate, setAddressToUpdate] = useState({ ...addressDetails });
  const [addressUpdatedErrors, setAddressUpdatedErrors] = useState({});

  const inputProps = {
    addressDetails: addressToUpdate,
    errors: addressUpdatedErrors,
    setErrors: setAddressUpdatedErrors,
    setAddressDetails: setAddressToUpdate,
  };

  const getFieldProps = (fieldName) => ({ ...inputProps, fieldName });

  const closeModal = () => {
    trackEvent('selectPlanCheckout.billingAddress.exit');
    if (!isEqual(addressToUpdate, addressDetails)) {
      setAddressToUpdate(addressDetails);
    }
    onClose();
  };

  const openModal = () => {
    trackEvent('selectPlanCheckout.confirmDetails.billingChangeClicked');
    setAddressUpdatedErrors({});
    onOpen();
  };

  const onBillingAddressUpdate = () => {
    const isValid = validateBillingAddress({
      addressToValidate: addressToUpdate,
      setErrors: setAddressUpdatedErrors,
      trackEvent,
    });

    if (isValid) {
      setSelectedBillingAddress(BILLING_ADDRESS);
      setBillingAddress(addressToUpdate);
      onClose && onClose();
    }
  };

  return (
    <>
      <BillingDetails addressDetails={addressDetails} openModal={openModal} />

      <EditModalWrapper
        handleSave={onBillingAddressUpdate}
        headerTitle="Edit billing address."
        isOpen={isOpen}
        onClose={closeModal}
      >
        <BillingAddressFormWrapper mt="0">
          <AddressInput {...getFieldProps(ADDRESS_FIELD)} />
          <AddressInput {...getFieldProps(ADDRESS_OPTIONAL_FIELD)} />
          <AddressInput {...getFieldProps(CITY_FIELD)} />
          <AddressInput
            {...getFieldProps(STATE_FIELD)}
            selectList={
              addressToUpdate.country === CANADIAN_COUNTRY_CODE
                ? CANADIAN_PROVINCES
                : US_STATES
            }
          />
          <AddressInput {...getFieldProps(POSTAL_CODE_FIELD)} />
          <AddressInput mb="0" {...getFieldProps(COUNTRY_FIELD)} />
        </BillingAddressFormWrapper>
      </EditModalWrapper>
    </>
  );
};
